import type { PostBotQueueRow, PostBotPublishMutation } from '../../lib/api-client.js';

const berlinParts = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export function berlinInput(iso: string): string {
  const date = new Date(iso);
  if (!Number.isFinite(+date)) return '';
  const p = Object.fromEntries(berlinParts.formatToParts(date).map(part => [part.type, part.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Enumerate both Berlin offsets so DST gaps and repeated hours are explicit. */
export function berlinInstants(value: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return [];
  const wall = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(wall)) return [];
  return [2, 1].map(offset => new Date(wall - offset * 3_600_000).toISOString())
    .filter(iso => berlinInput(iso) === value);
}

export function publishPending(row: PostBotQueueRow): boolean {
  return row.state === 'handoff_pending' || !!row.publishOperation &&
    !['confirmed', 'rejected', 'completed', 'failed'].includes(row.publishOperation.status);
}

/** Queue DTO omits completed operations; only a current revision can release our local lock. */
export function awaitingPublishConfirmation(row: PostBotQueueRow, revision: number | null): boolean {
  return revision !== null && ((row.publishRevision ?? -1) < revision || publishPending(row));
}

export function publishControls(row: PostBotQueueRow): { visible: boolean; disabled: boolean; canEdit: boolean; reason: string | null } {
  const visible = ['planned', 'captioning', 'ready', 'failed', 'skipped_no_caption', 'scheduled_on_maloum', 'handoff_pending', 'published'].includes(row.state);
  const reason = publishPending(row) ? 'Waiting for Maloum confirmation. Changes are temporarily locked.'
    : row.publishRevision === undefined ? 'Post controls are unavailable until the queue refreshes.'
    : ['scheduled_on_maloum', 'published'].includes(row.state) && row.publishDisabledReason
      ? `Changes on Maloum are disabled: ${row.publishDisabledReason.replaceAll('_', ' ')}.` : null;
  return { visible, disabled: !!reason, canEdit: !['captioning', 'published', 'handoff_pending'].includes(row.state), reason };
}

export function validatePublishEdit(caption: string, instant: string | undefined, now = Date.now()): { caption?: string; scheduledAt?: string } {
  const errors: { caption?: string; scheduledAt?: string } = {};
  if (!caption.trim()) errors.caption = 'Enter a nonempty caption.';
  else if (caption.trim().length > 2000) errors.caption = 'Use no more than 2,000 characters.';
  if (instant !== undefined) {
    const date = new Date(instant);
    if (!Number.isFinite(+date)) errors.scheduledAt = 'Choose a valid time.';
    else if (+date < now + 600_000) errors.scheduledAt = 'Choose a time at least 10 minutes from now.';
    else if (+date > now + 45 * 86_400_000) errors.scheduledAt = 'Choose a time within the next 45 days.';
    else if (date.getUTCMinutes() % 15 || date.getUTCSeconds() || date.getUTCMilliseconds()) errors.scheduledAt = 'Use a 15-minute interval (00, 15, 30 or 45).';
  }
  return errors;
}

export function publishMutationMessage(result: PostBotPublishMutation): string {
  if (result.status === 'uncertain') return 'Request submitted. Waiting for Maloum confirmation; changes are temporarily locked.';
  if (!result.changed) return 'No changes were made.';
  return result.status === 'cancelled' ? 'Post cancelled.' : 'Post updated.';
}
